import React, { useMemo, useState } from "react";
import { cellText } from "../utils/firestoreValueUtils";

export default function MatchingPreviewGrid({
  rows = [],
  showNewValue = true,
  selected,
  onToggleSelect,
  pageSize = 20,
}) {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("id");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(0);
  const [colWidths, setColWidths] = useState({});

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(
        (r) =>
          r.id.toLowerCase().includes(q) ||
          JSON.stringify(r.oldValue ?? r.before).toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const av = sortField === "id" ? a.id : JSON.stringify(a[sortField] ?? a.oldValue);
      const bv = sortField === "id" ? b.id : JSON.stringify(b[sortField] ?? b.oldValue);
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [rows, search, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const onSort = (col) => {
    if (sortField === col) setSortAsc((v) => !v);
    else {
      setSortField(col);
      setSortAsc(true);
    }
  };

  const resize = (col, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[col] || 140;
    const onMove = (ev) => {
      setColWidths((p) => ({ ...p, [col]: Math.max(80, startW + ev.clientX - startX) }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div className="fa-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
        <input
          className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          placeholder="Search preview…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <span className="text-xs text-slate-500">{filtered.length} row(s)</span>
      </div>
      <div className="fa-table-wrap max-h-96">
        <table className="fa-table">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr>
              {onToggleSelect && <th />}
              <th className="cursor-pointer" onClick={() => onSort("id")} style={{ width: colWidths.id || 120 }}>
                Document ID {sortField === "id" ? (sortAsc ? "↑" : "↓") : ""}
              </th>
              <th className="cursor-pointer" onClick={() => onSort("oldValue")} style={{ width: colWidths.old || 160 }}>
                Current value
                <span role="separator" className="ml-1 inline-block h-4 w-1 cursor-col-resize" onMouseDown={(e) => resize("old", e)} />
              </th>
              {showNewValue && (
                <th style={{ width: colWidths.new || 160 }}>New value</th>
              )}
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                {onToggleSelect && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selected?.has(r.id)}
                      onChange={() => onToggleSelect(r.id)}
                    />
                  </td>
                )}
                <td className="font-mono text-xs">{r.id}</td>
                <td className="max-w-xs truncate text-xs">{cellText(r.oldValue ?? r.before?.[Object.keys(r.before || {})[0]])}</td>
                {showNewValue && (
                  <td className="max-w-xs truncate text-xs text-emerald-700">{cellText(r.newValue)}</td>
                )}
                <td className="text-xs text-slate-500">{cellText(r.before?.createdAt || r.before?.created_at)}</td>
                <td className="text-xs text-slate-500">{cellText(r.before?.updatedAt || r.before?.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-slate-200 p-2 text-xs">
        <button type="button" className="fa-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</button>
        <span>Page {page + 1} / {totalPages}</span>
        <button type="button" className="fa-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
}
